import React, { useState } from "react";
import {
  Typography,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Box,
  Divider,
  Chip,
  TextField,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";

import CustomerLayout from "../CustomerLayout";

const OrderCustomization = () => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [saved, setSaved] = useState(false);


  const [loadDetails, setLoadDetails] = useState({
    weight: "",
    length: "",
    width: "",
    height: "",
    palletCount: "",
    requiredVehicleType: "",
    specialRestrictions: "",
  });

  const [additionalFeatures, setAdditionalFeatures] =
    useState({
      fragileHandling: false,
      expeditedService: false,
      insurance: false,
      signatureRequired: false,
      liftgateRequired: false,
      temperatureSensitive: false,
      noAnimalCargo: false,
      insidePickup: false,
      insideDropoff: false,
    });

  const loadTypeLabels = {
    standard_load: "Standard Load",
    fragile_load: "Fragile Load",
    oversized_load: "Oversized Load",
    refrigerated_load: "Refrigerated Load",
    hazmat_load: "Hazmat Load",
    business_load: "Business Load",
    residential_load: "Residential Load",
    high_value_load: "High-Value Load",
    time_sensitive_load: "Time-Sensitive Load",
  };

  const vehicleTypeLabels = {
    car: "Car",
    suv: "SUV",
    pickup_truck: "Pickup Truck",
    cargo_van: "Cargo Van",
    box_truck: "Box Truck",
    flatbed: "Flatbed",
    reefer: "Refrigerated Vehicle",
    semi_truck: "Semi Truck",
  };

  const handleOrderCustomization = () => {
    console.log("Load customization saved:", {
      selectedOptions,
      loadDetails,
      additionalFeatures,
    });

    setSaved(true);
  };

 const handleOptionChange = (event) => {
  const value = event.target.value;

  setSelectedOptions(value ? [value] : []);
};

  const handleLoadDetailChange =
    (field) => (event) => {
      setLoadDetails({
        ...loadDetails,
        [field]: event.target.value,
      });
    };

  const handleFeatureChange = (event) => {
    setAdditionalFeatures({
      ...additionalFeatures,
      [event.target.name]:
        event.target.checked,
    });
  };

  const selectedFeatureLabels = {
    fragileHandling: "Fragile Handling",
    expeditedService: "Expedited Service",
    insurance: "Insurance",
    signatureRequired:
      "Signature Required",
    liftgateRequired:
      "Liftgate Required",
    temperatureSensitive:
      "Temperature Sensitive",
    noAnimalCargo:
      "No Animal Cargo",
    insidePickup: "Inside Pickup",
    insideDropoff: "Inside Drop-off",
  };

  return (
    <CustomerLayout>
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            mb: { xs: 3, md: 4 },
            maxWidth: 760,
            mx: "auto",
            textAlign: {
              xs: "left",
              md: "center",
            },
          }}
        >
          <Typography
            variant="h4"
            fontWeight={800}
            gutterBottom
            sx={{
              fontSize: {
                xs: "2rem",
                md: "2.5rem",
              },
            }}
          >
            Load Customization
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
          >
            Add handling instructions,
            equipment needs, vehicle
            requirements, and special
            restrictions.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 2.25,
              sm: 3,
              md: 4,
            },
            borderRadius: 3,
            maxWidth: 920,
            mx: "auto",
            border: "1px solid #e2e8f0",
            boxShadow: {
              xs: "none",
              md: "0 10px 24px rgba(15, 23, 42, 0.06)",
            },
          }}
        >
          <Typography
            variant="h6"
            fontWeight={800}
          >
            Load Requirements
          </Typography>

          <Divider
            sx={{ mt: 1, mb: 3 }}
          />

          <Grid
            container
            spacing={2.5}
          >
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="customization-options-label">
                  Load Type / Requirements
                </InputLabel>

                <Select
  labelId="customization-options-label"
  value={selectedOptions[0] || ""}
  onChange={handleOptionChange}
  label="Load Type / Requirements"
>
  <MenuItem value="">
    Select load type
  </MenuItem>

  <MenuItem value="standard_load">
    Standard Load
  </MenuItem>

  <MenuItem value="fragile_load">
    Fragile Load
  </MenuItem>

  <MenuItem value="oversized_load">
    Oversized Load
  </MenuItem>

  <MenuItem value="refrigerated_load">
    Refrigerated Load
  </MenuItem>

  <MenuItem value="hazmat_load">
    Hazmat Load
  </MenuItem>

  <MenuItem value="business_load">
    Business Load
  </MenuItem>

  <MenuItem value="residential_load">
    Residential Load
  </MenuItem>

  <MenuItem value="high_value_load">
    High-Value Load
  </MenuItem>

  <MenuItem value="time_sensitive_load">
    Time-Sensitive Load
  </MenuItem>
</Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="required-vehicle-type-label">
                  Required Vehicle Type
                </InputLabel>

                <Select
                  labelId="required-vehicle-type-label"
                  value={
                    loadDetails.requiredVehicleType
                  }
                  onChange={handleLoadDetailChange(
                    "requiredVehicleType"
                  )}
                  label="Required Vehicle Type"
                >
                  <MenuItem value="">
                    Any suitable vehicle
                  </MenuItem>

                  <MenuItem value="car">
                    Car
                  </MenuItem>

                  <MenuItem value="suv">
                    SUV
                  </MenuItem>

                  <MenuItem value="pickup_truck">
                    Pickup Truck
                  </MenuItem>

                  <MenuItem value="cargo_van">
                    Cargo Van
                  </MenuItem>

                  <MenuItem value="box_truck">
                    Box Truck
                  </MenuItem>

                  <MenuItem value="flatbed">
                    Flatbed
                  </MenuItem>

                  <MenuItem value="reefer">
                    Refrigerated Vehicle
                  </MenuItem>

                  <MenuItem value="semi_truck">
                    Semi Truck
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ mt: 2 }}
              >
                Size & Weight
              </Typography>

              <Divider
                sx={{ mt: 1, mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Weight (lbs)"
                value={loadDetails.weight}
                onChange={handleLoadDetailChange(
                  "weight"
                )}
                fullWidth
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Length (in)"
                value={loadDetails.length}
                onChange={handleLoadDetailChange(
                  "length"
                )}
                fullWidth
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Width (in)"
                value={loadDetails.width}
                onChange={handleLoadDetailChange(
                  "width"
                )}
                fullWidth
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Height (in)"
                value={loadDetails.height}
                onChange={handleLoadDetailChange(
                  "height"
                )}
                fullWidth
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Pallet Count"
                value={
                  loadDetails.palletCount
                }
                onChange={handleLoadDetailChange(
                  "palletCount"
                )}
                fullWidth
                type="number"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ mt: 2 }}
              >
                Handling & Restrictions
              </Typography>

              <Divider
                sx={{ mt: 1, mb: 2 }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormGroup
                sx={{
                  maxWidth: 620,
                  mx: "auto",
                  alignItems:
                    "flex-start",
                }}
              >
                <Grid
                  container
                  spacing={1}
                >
                  {Object.entries(
                    additionalFeatures
                  ).map(
                    ([feature, checked]) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        key={feature}
                        sx={{
                          textAlign:
                            "left",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                checked
                              }
                              onChange={
                                handleFeatureChange
                              }
                              name={
                                feature
                              }
                            />
                          }
                          label={
                            selectedFeatureLabels[
                              feature
                            ]
                          }
                        />
                      </Grid>
                    )
                  )}
                </Grid>
              </FormGroup>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Special Restrictions / Notes"
                value={
                  loadDetails.specialRestrictions
                }
                onChange={handleLoadDetailChange(
                  "specialRestrictions"
                )}
                fullWidth
                multiline
                rows={3}
                placeholder="Example: no animal-related cargo due to allergies, appointment required, dock access, call before arrival..."
              />
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent={{
                  xs: "stretch",
                  sm: "center",
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={
                    handleOrderCustomization
                  }
                  fullWidth
                  sx={{
                    maxWidth: {
                      sm: 320,
                    },
                    textTransform:
                      "none",
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                >
                  Save Load Preferences
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Divider
                sx={{ my: 2 }}
              />

              <Typography
                variant="h6"
                fontWeight={800}
                gutterBottom
              >
                Selected Load Preferences
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {selectedOptions.map(
                  (option) => (
                    <Chip
                      key={option}
                      label={
                        loadTypeLabels[option] || option
                      }
                      color="primary"
                      variant="outlined"
                    />
                  )
                )}

                {loadDetails.requiredVehicleType && (
                  <Chip
                    label={`Vehicle: ${
                      vehicleTypeLabels[
                        loadDetails.requiredVehicleType
                      ] ||
                      loadDetails.requiredVehicleType.replaceAll(
                        "_",
                        " "
                      )
                    }`}
                    color="primary"
                    variant="outlined"
                  />
                )}

                {loadDetails.weight && (
                  <Chip
                    label={`Weight: ${loadDetails.weight} lbs`}
                    variant="outlined"
                  />
                )}

                {loadDetails.palletCount && (
                  <Chip
                    label={`Pallets: ${loadDetails.palletCount}`}
                    variant="outlined"
                  />
                )}

                {Object.entries(
                  additionalFeatures
                ).map(
                  ([feature, checked]) =>
                    checked ? (
                      <Chip
                        key={feature}
                        label={
                          selectedFeatureLabels[
                            feature
                          ]
                        }
                        color="primary"
                        variant="outlined"
                      />
                    ) : null
                )}

                {!selectedOptions.length &&
                  !Object.values(
                    additionalFeatures
                  ).some(Boolean) &&
                  !loadDetails.requiredVehicleType &&
                  !loadDetails.weight &&
                  !loadDetails.palletCount && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      No preferences selected yet.
                    </Typography>
                  )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Snackbar
          open={saved}
          autoHideDuration={3000}
          onClose={() => setSaved(false)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <Alert
            onClose={() => setSaved(false)}
            severity="success"
            variant="filled"
            sx={{ width: "100%" }}
          >
            Load preferences saved successfully.
          </Alert>
        </Snackbar>
      </Box>
    </CustomerLayout>
  );
};

export default OrderCustomization;